/**
 * capabilityGalleryState — cross-cutting state for the capability gallery
 * (Guided Tour Redesign, Part D3).
 *
 * Two independent concerns live here:
 *
 *   1. `capabilityGalleryOpen` — whether the gallery is showing. A plain
 *      signal (not local component state) because the gallery has THREE
 *      entry points that don't share a parent: HelpMenuButton.jsx (mounted
 *      in the CommandBar, nowhere near IonSurface), IonHome.jsx and
 *      IonIntroHandoverChips.jsx (both mounted INSIDE IonSurface). A signal
 *      lets every entry point open it directly, the same way components
 *      elsewhere read `chatStreaming`/`canSend` straight from chatStore.js
 *      rather than via props.
 *
 *   2. The gallery's OWN pre-session Explorer snapshot ("Back to my view" —
 *      spec Part D, point 4). See the long comment above
 *      `_gallerySessionBaseline` below for why this can't just reuse
 *      commandLayer.js's committedSnapshot/REVERT.
 */
import { signal, effect } from '@preact/signals'
import { chatMessages, ionSurface, openChat } from './chatStore.js'
import { snapshotExplorerState, restoreExplorerState } from './commandLayer.js'

// ============================================================================
// OPEN / CLOSE
// ============================================================================

export const capabilityGalleryOpen = signal(false)

/**
 * Opens the gallery. If the Ion panel is currently collapsed to the `rail`
 * strip, escalate it to `docked` first — the gallery renders INSIDE
 * `.ion-panel__surface` (IonSurface.jsx), which is visually collapsed via
 * CSS while `rail`, so opening the gallery without also opening the panel
 * would show nothing. Deliberately does NOT touch `docked`/`full` — both of
 * this function's other two callers (IonHome, the tour's handover step) are
 * already rendered from within an open panel, and forcing `full` back down
 * to `docked` would collapse a fullpage session the user didn't ask to leave.
 */
export function openCapabilityGallery() {
  if (ionSurface.peek() === 'rail') openChat()
  capabilityGalleryOpen.value = true
}

export function closeCapabilityGallery() {
  capabilityGalleryOpen.value = false
}

// ============================================================================
// GALLERY-SESSION BASELINE ("Back to my view")
// ============================================================================
//
// Every gallery replay drives the REAL Explorer through the same command
// layer a live turn uses (Part D1), so it IS revertible — via the existing
// "Ion: ... · REVERT" attribution card (Part D2). But commandLayer.js keeps
// exactly ONE committed snapshot slot, not a stack (see its own comment
// above `committedSnapshot`): playing two recordings back to back means
// REVERT only undoes the SECOND one, landing wherever the FIRST replay left
// things — confirmed in tests/explorer-commands/capability-gallery-revert.
// test.js. That is a known, reported limitation of the SHARED command layer,
// and BA's instruction is explicitly NOT to fix it by turning it into a
// stack there.
//
// So the gallery keeps its OWN, separate snapshot instead — captured ONCE,
// the FIRST time a recording is played in a gallery session (not merely on
// gallery OPEN — looking around without playing anything must not anchor
// anything), via commandLayer.js's snapshotExplorerState()/
// restoreExplorerState(). Those are the exact same standalone
// (non-transactional) pair ion-intro's own tour baseline already uses
// (tourDefinitions.js), for the identical reason: a caller that isn't a live
// Ion turn needs snapshot/restore without touching openSnapshot/
// committedSnapshot. "Back to my view" restores it, one-shot — restoring
// clears the baseline (mirrors revertIonTransaction: a second click with no
// new play in between is a no-op, not a re-restore).
//
// Discarding a STALE baseline: a baseline that lives forever would, much
// later, silently blow away real work the user did after browsing the
// gallery — a manual filter change, or a genuine question asked of Ion —
// back to the stale pre-gallery state. That is worse than no "Back to my
// view" at all, so the baseline is discarded (WITHOUT restoring anything)
// the moment the user sends a REAL turn of their own. Detected by watching
// chatMessages for a new USER message that does NOT carry the `example`
// flag: every replayed question carries `{ example: true }` (Part D1,
// reusing S4's existing marker), every real one doesn't — so this reuses
// that existing signal instead of adding a second "is this real" marker.
// Closing the gallery does NOT discard it: the user may reopen the gallery
// later and still click "Back to my view" without having sent anything
// real in between.

let _gallerySessionBaseline = null

/** Reactive mirror of "is a baseline currently held" — drives the "Back to
 *  my view" button's visibility/enabled state. Kept in lockstep with
 *  `_gallerySessionBaseline` by every function below that touches it. */
export const gallerySessionBaselineHeld = signal(false)

let _prevMessageCount = chatMessages.peek().length
effect(() => {
  const messages = chatMessages.value
  if (messages.length > _prevMessageCount && _gallerySessionBaseline !== null) {
    const newMessages = messages.slice(_prevMessageCount)
    const realTurnSent = newMessages.some(
      (m) => m.role === 'user' && m.meta?.example !== true
    )
    if (realTurnSent) {
      _gallerySessionBaseline = null
      gallerySessionBaselineHeld.value = false
    }
  }
  _prevMessageCount = messages.length
})

/**
 * Capture the Explorer state ONCE per gallery session. A no-op if a
 * baseline is already held — only the FIRST play in a session anchors it;
 * later plays in the same session must not keep moving the anchor forward,
 * or "Back to my view" would only undo the last one (the exact composition
 * problem this file exists to avoid for the gallery's own affordance).
 */
export function captureGallerySessionBaselineIfNeeded() {
  if (_gallerySessionBaseline === null) {
    _gallerySessionBaseline = snapshotExplorerState()
    gallerySessionBaselineHeld.value = true
  }
}

/**
 * "Back to my view" — restore the gallery-session baseline and consume it.
 * No-op if nothing has been played yet this session (no baseline held).
 */
export function restoreGallerySessionBaseline() {
  if (_gallerySessionBaseline === null) return
  const snapshot = _gallerySessionBaseline
  _gallerySessionBaseline = null
  gallerySessionBaselineHeld.value = false
  restoreExplorerState(snapshot)
}

// Exported for tests only — module-level `let`s otherwise leak state
// between test files/cases that don't each get a fresh module instance.
export const __test__ = {
  reset() {
    _gallerySessionBaseline = null
    gallerySessionBaselineHeld.value = false
    _prevMessageCount = chatMessages.peek().length
  },
  hasBaseline() {
    return _gallerySessionBaseline !== null
  },
}
